import {FC} from "react";
import {useNavigate} from "react-router-dom";
import {Stack, IconButton, Tooltip} from "@mui/material";
import {styled} from "@mui/material/styles";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import {useAppDispatch} from "../../../hooks/redux";
import {usePermissions} from "../../../hooks/usePermissions";
import {canRoute} from "../../../models/permissions";
import {fetchOut} from "../../../features/auth/model/actions";
import ColorModeIconDropdown from "../../ColorModeIconDropdown";
import CompanyIndicator from "./CompanyIndicator";
import {routes} from "../../../utils/routes";
import {t} from "../../../styles/theme/tokens";
import {SIDEBAR_FOOTER_HEIGHT} from "./constants";

/**
 * ============================================================================
 * MenuPanel — подвал sidebar'a: контекст компании + глобальные действия.
 * ============================================================================
 *
 * Структура (два яруса):
 *   ┌──────────────────────────────┐
 *   │ CompanyIndicator (одна строка)│  ← название текущей компании, ellipsis
 *   ├──────────────────────────────┤
 *   │ [🌓] [👤]                    │  ← ряд глобальных действий
 *   └──────────────────────────────┘
 *
 * Почему два яруса, а не одна строка:
 *   Иконки разложены space-between на 240px. Втиснуть туда ещё и название
 *   компании (которое бывает длинным) нельзя — либо оно отберёт место у
 *   иконок, либо обрежется до бесполезности. Поэтому компания получает
 *   отдельную полную-ширину строку сверху, иконки остаются как были.
 *   Читается как «контекст → действия».
 *
 * Высота подвала увеличена с 48 до SIDEBAR_FOOTER_HEIGHT (теперь 72) под
 * второй ярус — значение централизовано в constants.
 *
 * Действия (слева направо):
 *   1. Theme switch — переключение light/dark.
 *   2. Profile — навигация на профиль (личные данные), если маршрут доступен роли.
 *   3. Выход — у всех ролей (S20.2: у ограниченных ролей профиля нет, выход — здесь).
 *
 * onItemClick:
 *   Прокидывается из мобильного drawer'а (SideMenuMobile). Вызывается ПОСЛЕ
 *   каждого действия, чтобы drawer закрывался так же, как при клике на
 *   обычный пункт меню. На десктопе проп не передаётся (drawer permanent) —
 *   тогда это no-op.
 * ============================================================================
 */

interface MenuPanelProps {
    /** Вызывается после любого действия — для закрытия mobile drawer'а. */
    onItemClick?: () => void;
}

const PanelRoot = styled("div")({
    height: SIDEBAR_FOOTER_HEIGHT,
    paddingInline: 12,
    paddingBlock: 8,
    flexShrink: 0,
    borderTop: `1px solid ${t.divider}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
});

const ActionsRow = styled(Stack)({
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
});

const MenuPanel: FC<MenuPanelProps> = ({onItemClick}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // Профиль — если маршрут доступен роли; «Выход» — всем (ROLES.md § 2, S20.2).
    const hasProfile = canRoute(usePermissions(), routes.profile);

    const profileClickHandler = () => {
        navigate(`${routes.profile}?tab=0`);
        onItemClick?.();
    };

    const logoutClickHandler = () => {
        onItemClick?.();
        dispatch(fetchOut());
    };

    return (
        <PanelRoot>
            <CompanyIndicator/>

            <ActionsRow>
                {/* Переключатель темы. Закрытие drawer'а при смене темы не
                    делаем: пользователь может захотеть остаться в меню и
                    оценить его в новой теме. */}
                <ColorModeIconDropdown/>

                <Stack direction="row" sx={{gap: 0.5}}>
                    {hasProfile && (
                        <Tooltip title="Профиль">
                            <IconButton
                                size="small"
                                onClick={profileClickHandler}
                                aria-label="Профиль"
                            >
                                <AccountCircleOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Выход">
                        <IconButton
                            size="small"
                            onClick={logoutClickHandler}
                            aria-label="Выход"
                        >
                            <LogoutRoundedIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>

            </ActionsRow>
        </PanelRoot>
    );
};

export default MenuPanel;